/**
 * Concession consent scripts — LOC-1668 / LOC-1684
 *
 * DO NOT EDIT THE STRINGS BELOW WITHOUT LEGAL/COMPLIANCE SIGN-OFF.
 * Wording is locked per ticket AC. Any change requires a new sign-off
 * recorded against this file in source control.
 *
 * LOC-1684 (post L&C review): combined the two Services Australia authorise
 * bullets into one; removed italics from the NSW "For Service NSW" section;
 * made the NSW final declare and the VIC notify/understand/declare statements
 * bullets. Bullet placement assumed from ticket wording (screenshots not
 * machine-readable) — VERIFY against LOC-1684 screenshots before sign-off.
 *
 * Per-state consent text rendered between the "Concession and rebate
 * information" alert (LOC-1666) and the two electronic-consent checkboxes
 * (LOC-1655). SA does not render this block. QLD branches on
 * `concessionCardType`: QLD_SENIORS_CARD uses the seniors variant, all
 * other QLD cards use the variant with an appended Services Australia
 * section.
 *
 * Structure:
 *   ConsentScript    — one screen-level block per state/variant
 *     heading        — alert title
 *     sections[]     — grouped paragraphs (each may have a subheading)
 *       subheading   — optional inline section title (e.g. "For Services Australia")
 *       paragraphs[] — body lines or bullet items
 *         text           — paragraph string
 *         boldPhrases    — substrings within `text` wrapped in <strong>
 *         italic         — whole paragraph wrapped in <em>
 *         bold           — whole paragraph wrapped in <strong>
 *         variant        — "body" (default) or "list-item" (bullet)
 */

import { State } from "../../Premises/types";

export interface ConsentParagraph {
  text: string;
  boldPhrases?: string[];
  italic?: boolean;
  bold?: boolean;
  variant?: "body" | "list-item";
}

export interface ConsentSection {
  subheading?: string;
  paragraphs: ConsentParagraph[];
}

export interface ConsentScript {
  heading: string;
  sections: ConsentSection[];
}

const CONSENT_HEADING = "We need consent to validate your concession";

const SERVICES_AUSTRALIA_AUTH_INTRO = "By submitting this form, you authorise:";

// two authorise statements combined into one bullet (VIC/NSW/QLD/ACT).
const SERVICES_AUSTRALIA_AUTH_BULLETS: ConsentParagraph[] = [
  {
    text: "Origin to use Centrelink Confirmation eServices to perform a Centrelink/DVA enquiry of my Centrelink or Department of Veterans' Affairs customer details and concession card status to enable the business to determine if I qualify for a concession, rebate or service. Services Australia to provide the results of that enquiry to Origin.",
    variant: "list-item",
  },
];

// NSW
const NSW_CONSENT_SCRIPT: ConsentScript = {
  heading: CONSENT_HEADING,
  sections: [
    {
      subheading: "For Services Australia",
      paragraphs: [
        { text: SERVICES_AUSTRALIA_AUTH_INTRO },
        ...SERVICES_AUSTRALIA_AUTH_BULLETS,
        { text: "I understand that:" },
        {
          text: "Services Australia will disclose personal information to Origin including my file number / name / address / postcode / concession card type and status to confirm my eligibility for the relevant NSW rebate or service.",
          boldPhrases: ["relevant NSW rebate or service."],
          variant: "list-item",
        },
        {
          text: "This consent, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin or Services Australia. I can get proof of my circumstances or details from Services Australia and provide it to Origin so they can determine my eligibility for the relevant NSW rebate or service.",
          boldPhrases: ["relevant NSW rebate or service."],
          variant: "list-item",
        },
        {
          text: "If I withdraw my consent or don't alternatively provide proof of my circumstances or details, I may not be eligible for the relevant NSW rebate or service provided by Origin.",
          boldPhrases: ["relevant NSW rebate or service"],
          variant: "list-item",
        },
        {
          text: "Origin will disclose the results of the Centrelink/DVA enquiry to Service NSW for the purposes of confirming your eligibility for, and/or internal administration of, the concession or rebate.",
          boldPhrases: [
            "Service NSW",
            "confirming your eligibility for, and/or internal administration of, the concession or rebate",
          ],
          variant: "list-item",
        },
      ],
    },
    {
      subheading: "For Service NSW",
      paragraphs: [
        {
          text: "I advise that the above address is my principal place of residence and is the only residence within New South Wales for which the rebate is claimed by me and the above electricity or gas account is solely or jointly in my name.",
          boldPhrases: ["New South Wales"],
        },
        {
          text: "I will notify Origin immediately of any change in my circumstances which may affect my eligibility for the rebate (as applicable).",
          variant: "list-item",
        },
        { text: "I authorise:" },
        {
          text: "Origin to disclose my personal information to Services Australia, and the Commonwealth Department of Veterans' Affairs for the purpose of confirming my eligibility for the rebate.",
          variant: "list-item",
        },
        {
          text: "Services Australia, and the Commonwealth Department of Veterans' Affairs to provide the results of that enquiry to Origin.",
          variant: "list-item",
        },
        {
          text: "Origin to share the information I provide with the Department of Climate Change, Energy, the Environment and Water (DCCEEW) and Service NSW and to any other person as required by law.",
          variant: "list-item",
        },
        { text: "I understand that:", bold: true },
        {
          text: "If I am eligible for the Low Income Household Rebate or the NSW Gas Rebate and I'm sold both gas and electricity by Origin, I am eligible for both the Low Income Household Rebate and the NSW Gas Rebate.",
          variant: "list-item",
        },
        {
          text: "This consent to the eligibility check, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin.",
          variant: "list-item",
        },
        {
          text: "I can revoke my consent to the eligibility details being used at any time in writing, but if I withdraw my consent, I may not be eligible for the rebate unless I can provide written verification of my continuing eligibility from Services Australia or the Commonwealth Department of Veterans' Affairs.",
          variant: "list-item",
        },
        {
          text: "I declare that, to the best of my knowledge, the information I have provided is true and correct.",
          variant: "list-item",
        },
      ],
    },
  ],
};

// ACT
const ACT_CONSENT_SCRIPT: ConsentScript = {
  heading: CONSENT_HEADING,
  sections: [
    {
      subheading: "For Services Australia",
      paragraphs: [
        { text: SERVICES_AUSTRALIA_AUTH_INTRO },
        ...SERVICES_AUSTRALIA_AUTH_BULLETS,
        { text: "I understand that:" },
        {
          text: "Services Australia will disclose personal information to Origin including my file number / name / address / postcode / concession card type and status to confirm my eligibility for the relevant ACT rebate.",
          boldPhrases: ["the relevant ACT rebate."],
          variant: "list-item",
        },
        {
          text: "This consent, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin or Services Australia. I can get proof of my circumstances or details from Services Australia and provide it to Origin so they can determine my eligibility for the relevant ACT rebate.",
          boldPhrases: ["the relevant ACT rebate."],
          variant: "list-item",
        },
        {
          text: "If I withdraw my consent or don't alternatively provide proof of my circumstances or details, I may not be eligible for the relevant ACT rebate provided by Origin.",
          boldPhrases: ["the relevant ACT rebate"],
          variant: "list-item",
        },
        {
          text: "Origin will disclose personal information from the results of the Centrelink/DVA enquiry to ACT Government for the purposes of confirming your eligibility for, and/or internal administration of, the concession or rebate.",
          boldPhrases: [
            "ACT Government",
            "confirming your eligibility for, and/or internal administration of, the concession or rebate",
          ],
          variant: "list-item",
        },
      ],
    },
    {
      subheading: "For ACT:",
      paragraphs: [
        { text: "The rebate is funded by the ACT Government." },
        {
          text: "I declare that the information that I have given is true and correct.",
          bold: true,
        },
      ],
    },
  ],
};

// VIC
const VIC_CONSENT_SCRIPT: ConsentScript = {
  heading: CONSENT_HEADING,
  sections: [
    {
      subheading: "For Services Australia",
      paragraphs: [
        { text: SERVICES_AUSTRALIA_AUTH_INTRO },
        ...SERVICES_AUSTRALIA_AUTH_BULLETS,
        { text: "I understand that:", bold: true },
        {
          text: "Services Australia will disclose personal information to Origin including my file number / name / address / postcode / concession card type and status to confirm my eligibility for the relevant Victorian concession or service.",
          boldPhrases: ["relevant Victorian concession or service."],
          variant: "list-item",
        },
        {
          text: "This consent, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin or Services Australia. I can get proof of my circumstances or details from Services Australia and provide it to Origin so they can determine my eligibility for the relevant Victorian concession or service.",
          boldPhrases: ["relevant Victorian concession or service."],
          variant: "list-item",
        },
        {
          text: "If I withdraw my consent or don't alternatively provide proof of my circumstances or details, I may not be eligible for the relevant Victorian concession or service provided by Origin.",
          boldPhrases: ["relevant Victorian concession or service"],
          variant: "list-item",
        },
        {
          text: "Origin will disclose personal information from the results of the Centrelink/DVA enquiry to Department of Families, Fairness and Housing (DFFH) for the purposes of confirming your eligibility for, and/or internal administration of, the concession or rebate.",
          boldPhrases: [
            "Department of Families, Fairness and Housing (DFFH)",
            "confirming your eligibility for, and/or internal administration of, the concession or rebate",
          ],
          variant: "list-item",
        },
      ],
    },
    {
      subheading: "For The Department of Families, Fairness & Housing",
      paragraphs: [
        {
          text: "I advise that the above address is my principal place of residence and is the only residence within Victoria for which the rebate is claimed by me and the above electricity or gas account is solely or jointly in my name.",
          boldPhrases: ["Victoria"],
        },
        {
          text: "I will notify Origin immediately of any change in my circumstances which may affect my eligibility for the concession.",
          variant: "list-item",
        },
        {
          text: "I understand that concessions are provided by the State of Victoria.",
          variant: "list-item",
        },
        {
          text: "I declare that all the information that I have given is true and correct.",
          variant: "list-item",
        },
      ],
    },
  ],
};

// QLD common body (shared across both QLD variants)
const QLD_COMMON_PARAGRAPHS: ConsentParagraph[] = [
  {
    text: "By submitting this form, I advise that the above address is my principal place of residence and is the only residence within Queensland for which the rebate is claimed by me and the above electricity or gas account (as applicable) is solely or jointly in my name.",
    boldPhrases: ["Queensland"],
  },
  {
    text: "I will notify Origin immediately of any change in my circumstances which may affect my eligibility for the electricity and/or reticulated natural gas rebate (as applicable).",
    boldPhrases: ["electricity and/or reticulated natural gas rebate"],
    variant: "list-item",
  },
  { text: "I authorise:", bold: true },
  {
    text: "Origin to disclose my personal information to the Department of Home Affairs (DHA) or Department of Families, Seniors, Disability Services and Child Safety (DFSDSCS) and their service delivery agent Department of Customer Service, Open Data, Small and Family Business (DCDSB) to confirm my eligibility for the electricity or reticulated natural gas rebate.",
    boldPhrases: [
      "DHA",
      "DFSDSCS",
      "DCDSB",
      "electricity or reticulated natural gas rebate",
    ],
    variant: "list-item",
  },
  {
    text: "DHA or DFSDSCS to provide the results of that enquiry to Origin.",
    variant: "list-item",
  },
  { text: "I understand that:" },
  {
    text: "DHA or DFSDSCS will use information I have provided to Origin to confirm my eligibility for the electricity or reticulated natural gas rebate and will disclose to Origin personal information including my name, address and card number and status.",
    variant: "list-item",
  },
  {
    text: "This consent, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin.",
    variant: "list-item",
  },
  {
    text: "I can obtain proof of my circumstances/details from DHA or DFSDSCS (as applicable) and provide it to Origin so that my eligibility for the electricity or reticulated natural gas rebate can be determined.",
    boldPhrases: ["electricity or reticulated natural gas rebate"],
    variant: "list-item",
  },
  {
    text: "If I withdraw my consent or do not alternatively provide proof of my circumstances/details, I may not be eligible for the electricity or reticulated natural gas rebate provided by the Queensland Government.",
    variant: "list-item",
  },
  {
    text: "I declare that all the information that I have given is true and correct.",
  },
];

// QLD (QLD Seniors card)
const QLD_SENIORS_CONSENT_SCRIPT: ConsentScript = {
  heading: CONSENT_HEADING,
  sections: [{ paragraphs: QLD_COMMON_PARAGRAPHS }],
};

// QLD (All other Cards)
const QLD_OTHER_CONSENT_SCRIPT: ConsentScript = {
  heading: CONSENT_HEADING,
  sections: [
    { paragraphs: QLD_COMMON_PARAGRAPHS },
    {
      subheading: "For Services Australia",
      paragraphs: [
        { text: "By submitting this form, I authorise:" },
        ...SERVICES_AUSTRALIA_AUTH_BULLETS,
        { text: "I understand that:" },
        {
          text: "Services Australia will disclose personal information to Origin including my file number / name / address / postcode / concession card type and status to confirm my eligibility for the relevant Queensland concession, rebate or service.",
          boldPhrases: ["relevant Queensland concession, rebate or service."],
          variant: "list-item",
        },
        {
          text: "This consent, once signed, remains valid while I am a customer of Origin unless I withdraw it by contacting Origin or Services Australia. I can get proof of my circumstances or details from Services Australia and provide it to Origin so they can determine my eligibility for the relevant Queensland concession, rebate or service.",
          boldPhrases: ["relevant Queensland concession, rebate or service."],
          variant: "list-item",
        },
        {
          text: "If I withdraw my consent or don't alternatively provide proof of my circumstances or details, I may not be eligible for the relevant Queensland concession, rebate or service provided by Origin.",
          boldPhrases: ["relevant Queensland concession, rebate or service"],
          variant: "list-item",
        },
        {
          text: "Origin will disclose personal information from the results of the Centrelink/DVA enquiry to the Department of Families, Seniors, Disability Services and Child Safety (DFSDSCS) for the purposes of confirming your eligibility for, and/or internal administration of, the concession or rebate.",
          boldPhrases: [
            "Department of Families, Seniors, Disability Services and Child Safety",
            "DFSDSCS",
            "confirming your eligibility for, and/or internal administration of, the concession or rebate",
          ],
          variant: "list-item",
        },
      ],
    },
  ],
};

export const getConcessionConsentScript = (
  state: State | "",
  cardType: string
): ConsentScript | null => {
  if (!state || state === State.SA) return null;
  if (state === State.QLD) {
    return cardType === "QLD_SENIORS_CARD"
      ? QLD_SENIORS_CONSENT_SCRIPT
      : QLD_OTHER_CONSENT_SCRIPT;
  }
  if (state === State.NSW) return NSW_CONSENT_SCRIPT;
  if (state === State.ACT) return ACT_CONSENT_SCRIPT;
  if (state === State.VIC) return VIC_CONSENT_SCRIPT;
  return null;
};

export const shouldShowServicesAustraliaConsent = (
  state: State | "",
  cardType: string
): boolean => {
  if (!state || state === State.SA) return false;
  if (state === State.QLD) return cardType !== "QLD_SENIORS_CARD";
  return state === State.NSW || state === State.ACT || state === State.VIC;
};
